import {type SiretLookupResponse, SiretLookupSiretSchema} from "@mytour/shared";
import {useQuery} from "@tanstack/react-query";
import {getSiretLookup} from "./services/siret.service.js";

export const siretLookupKeysStore = {
  root: ["siretLookup"] as const,
  lookup: (siret: string) => [...siretLookupKeysStore.root, siret] as const,
};

/**
 * Reactively looks up a company by SIRET. The query is enabled only when the
 * SIRET is exactly 14 digits AND the caller opts in via `enabled` — this lets
 * the consumer gate the lookup behind a feature flag (e.g. `siret-autofill`)
 * without coupling this generic hook to any specific flag.
 *
 * A SIRET identifies a fixed legal entity, so the result never goes stale:
 * `staleTime: Infinity` and `retry: false` (a failed lookup just means manual
 * entry — no point retrying or refetching).
 *
 * `siretLookup` is exposed only while the query is active: React Query keeps
 * returning cached `data` for a disabled query, so we coalesce it to `undefined`
 * when the lookup is gated off — otherwise a SIRET cached while the flag was on
 * would still surface after the caller disables it.
 */
export function useSiretLookup(siret: string, enabled = true) {
  const isActive = enabled && SiretLookupSiretSchema.safeParse(siret).success;

  const {data, isLoading, isFetching} = useQuery<SiretLookupResponse>({
    queryKey: siretLookupKeysStore.lookup(siret),
    queryFn: () => getSiretLookup(siret),
    enabled: isActive,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    siretLookup: isActive ? data : undefined,
    isSiretLookupLoading: isLoading,
    isSiretLookupFetching: isFetching,
  };
}
